/**
 * The host operator's landing page: is anything on this server wrong?
 *
 * One request (`GET /api/host/dashboard`) reads the server itself and every organization on it. The
 * page puts the server's own state first - version, uptime, disk, updates - then what needs a host
 * operator's attention, then a row per organization with its fleet, tickets, receivables and the
 * state of its backups, then the host's recent activity. Managing organizations stays on the
 * Organizations page; a row here opens the organization, as there.
 *
 * Re-read every minute while open.
 */
import { insets, type Container } from 'webwin';
import * as auth from '../api/auth.js';
import * as host from '../api/host.js';
import { describeError } from '../api/http.js';
import { TenantStatus, type HostDashboardDto, type HostEventDto, type HostTenantHealthDto } from '../contracts.gen.js';
import { ago, bytes, join, localDateTime, money, plural } from '../format.js';
import { backupLine, diskShare, diskTone, duration, hostAttention, hostTotals, type HostAttention } from '../hostDashboard.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { stat } from '../ui/Stat.js';
import { StatusLine } from '../ui/StatusLine.js';
import { UsageBar } from '../ui/UsageBar.js';
import {
  button,
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const REFRESH_MS = 60_000;

export function hostDashboardPage(): CrmPage {
  return {
    mount(host_: Container, ctx: PageContext) {
      let busy = false;
      const status = new StatusLine();
      const subtitle = muted('');

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Server dashboard'),
        subtitle,
        stackOf(buttonRow(), button('Refresh', () => void load())),
        status.label
      );

      // ---- headline figures ----
      const orgs = stat('Organizations', 160);
      const customers = stat('Customers', 160);
      const agents = stat('Agents online', 160);
      const tickets = stat('Open tickets', 160);
      const storage = stat('Data on disk', 160);
      const figures = card();
      figures.controls.add(
        stackOf(
          buttonRow({ spacing: GAP.lg, lineSpacing: GAP.md, align: 'start' }),
          orgs.panel,
          customers.panel,
          agents.panel,
          tickets.panel,
          storage.panel
        )
      );

      // ---- the server ----
      const serverLines = vstack({ spacing: GAP.xs });
      const diskBar = new UsageBar();
      const diskText = muted('');
      const updateLine = muted('');
      const server = section(
        'This server',
        ['Server updates →', () => ctx.go('/server-updates')],
        serverLines,
        stackOf(vstack({ spacing: GAP.xs }), text('Data drive', { font: FONT.small }), diskBar, diskText),
        updateLine
      );

      // ---- needs attention ----
      const attentionEmpty = new EmptyState('Nothing needs attention right now.');
      const attention = new CardList<HostAttention>({ key: a => a.key, build: a => attentionRow(a), spacing: GAP.sm });
      const attentionCard = section('Needs attention', null, attention.panel, attentionEmpty.panel);

      // ---- organizations ----
      const tenantEmpty = new EmptyState('No organizations yet.');
      const tenantList = new CardList<HostTenantHealthDto>({
        key: t => t.id,
        version: t => JSON.stringify(t),
        build: t => tenantRow(t),
        spacing: GAP.sm
      });
      const tenantCard = section('Organizations', ['Manage →', () => ctx.go('/organizations')], tenantList.panel, tenantEmpty.panel);

      // ---- recent activity ----
      const events = new CardList<HostEventDto>({ key: e => e.id, build: e => eventRow(e), spacing: GAP.xs });
      const eventsEmpty = new EmptyState('Nothing has happened yet.');
      const activity = section('Recent host activity', null, events.panel, eventsEmpty.panel);

      const body = stackOf(vstack({ spacing: GAP.lg, visible: false }), figures, server, attentionCard, tenantCard, activity);
      const { view, content } = scrollPage();
      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), header, body));
      host_.controls.add(view);

      // =================================================================

      function section(
        title: string,
        more: readonly [string, () => void] | null,
        ...children: Parameters<typeof stackOf>[1][]
      ): Card {
        const heading = splitRow({ align: 'center' });
        fillWith(heading, h2(title));
        if (more) heading.controls.add(link(more[0], more[1]));
        const shell = card();
        shell.controls.add(stackOf(vstack({ spacing: GAP.md }), heading, ...children));
        return shell;
      }

      function attentionRow(a: HostAttention): Card {
        const left = stackOf(vstack({ spacing: GAP.xs }), value(a.title, { font: FONT.bold }));
        if (a.detail) left.controls.add(muted(a.detail));
        const row = splitRow({ align: 'center', spacing: GAP.md });
        row.controls.add(chip(a.tag, a.tone));
        fillWith(row, left);
        const shell = card({ padding: insets(10, 8), cursor: 'pointer' });
        shell.controls.add(row);
        // Fixed inside the organization: open it there. Otherwise it is fixed from a host page.
        shell.onActivate(() =>
          a.tenantId !== null && a.tenantPath !== null
            ? void enter(a.tenantId, a.tag, a.tenantPath)
            : ctx.go(a.hostPath ?? (a.tenantId !== null ? '/organizations' : '/server-updates'))
        );
        return shell;
      }

      function tenantRow(t: HostTenantHealthDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(t.name, { font: FONT.bold }));
        if (t.isDefault) heading.controls.add(chip('default', 'brand'));
        if (t.status === TenantStatus.Suspended) heading.controls.add(chip('suspended', 'warn'));
        else if (!t.running) heading.controls.add(chip('not running', 'danger'));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        if (t.running) {
          body.controls.add(
            text(
              join([
                plural(t.customers ?? 0, 'customer'),
                `${t.agentsOnline ?? 0} of ${plural(t.devices ?? 0, 'device')} online`,
                (t.pendingDevices ?? 0) > 0 ? `${t.pendingDevices} awaiting approval` : null,
                plural(t.staffUsers ?? 0, 'staff account')
              ]),
              { font: FONT.small }
            )
          );
          body.controls.add(
            muted(
              join([
                `${plural(t.openTickets ?? 0, 'open ticket')}${(t.urgentTickets ?? 0) > 0 ? ` (${t.urgentTickets} high or urgent)` : ''}`,
                `${money(t.outstandingTotal)} outstanding`,
                (t.overdueInvoices ?? 0) > 0 ? `${money(t.overdueTotal)} overdue` : null
              ])
            )
          );
          if ((t.lockedAccounts ?? 0) > 0) body.controls.add(muted(`${plural(t.lockedAccounts ?? 0, 'account')} locked out`, 'warn'));
        } else {
          body.controls.add(muted(t.startError ? 'Start failed: ' + t.startError : 'Not running', t.startError ? 'danger' : 'muted'));
        }
        const backup = backupLine(t);
        const backupRow = buttonRow({ spacing: GAP.sm });
        if (t.running) {
          backupRow.controls.add(chip(backup.tone === 'ok' ? 'backups ok' : backup.tone === 'danger' ? 'backup failed' : 'check backups', backup.tone));
        }
        backupRow.controls.add(muted(join([t.running ? backup.text : null, `database ${bytes(t.databaseBytes)}`])));
        body.controls.add(backupRow);

        const shell = card({ tint: 'alt', cursor: t.running ? 'pointer' : 'default' });
        shell.controls.add(body);
        if (t.running) shell.onActivate(() => void enter(t.id, t.name, '/dashboard'));
        return shell;
      }

      function eventRow(e: HostEventDto): Card {
        const shell = card({ tint: 'alt', padding: insets(10, 6) });
        shell.controls.add(
          stackOf(vstack({ spacing: 0 }), text(e.summary, { font: FONT.small }), muted(join([localDateTime(e.whenUtc), e.actor, e.detail ?? null])))
        );
        return shell;
      }

      async function enter(id: number, name: string, path: string): Promise<void> {
        if (busy) return;
        busy = true;
        status.busy(`Opening ${name}…`);
        try {
          await auth.enterTenant(id);
          // The shell hears the session change and rebuilds the rail for the organization.
          ctx.go(path, { replace: true });
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      function render(d: HostDashboardDto): void {
        const s = d.server;
        const totals = hostTotals(d.tenants);
        subtitle.text = join([`Version ${s.version}`, s.machineName, `up ${duration(s.startedUtc)}`, `as of ${localDateTime(d.generatedUtc)}`]);

        orgs.set(
          String(totals.organizations),
          join([`${totals.running} running`, totals.suspended > 0 ? `${totals.suspended} suspended` : null]),
          totals.running < totals.organizations - totals.suspended ? 'danger' : 'text'
        );
        customers.set(String(totals.customers), plural(totals.staff, 'staff account'));
        agents.set(
          `${totals.online} of ${totals.devices}`,
          totals.pending > 0 ? `${totals.pending} awaiting approval` : `${totals.devices - totals.online} offline`
        );
        tickets.set(String(totals.openTickets), 'across every organization');
        storage.set(bytes(s.dataBytes), `${plural(s.hostOperators, 'host operator')}`);

        serverLines.controls.clear();
        serverLines.controls.add(text(join([`Version ${s.version}`, s.environment]), { font: FONT.small }));
        serverLines.controls.add(muted(join([s.machineName, s.operatingSystem])));
        serverLines.controls.add(muted(join([`Started ${localDateTime(s.startedUtc)}`, `up ${duration(s.startedUtc)}`, `${bytes(s.workingSetBytes)} of memory in use`])));

        const used = diskShare(s);
        diskBar.percent = used ?? 0;
        diskBar.tone = diskTone(used);
        diskBar.invalidate();
        diskText.text = used === null
          ? 'The free space on the data drive could not be read.'
          : `${bytes(s.diskFreeBytes)} free of ${bytes(s.diskTotalBytes)} (${Math.round(used)}% used)`;

        if (!s.updatesManaged) {
          updateLine.text = 'Updates are managed by the NAS install; this run is not supervised.';
          updateLine.role = 'muted';
        } else if (s.lastUpdateFailure) {
          updateLine.text = 'The last update failed and was rolled back: ' + s.lastUpdateFailure;
          updateLine.role = 'danger';
        } else if (s.updateAvailable && s.latestVersion) {
          updateLine.text = `Version ${s.latestVersion} is available.`;
          updateLine.role = 'warn';
        } else {
          updateLine.text = join(['Up to date', s.lastUpdateCheckUtc ? `checked ${ago(s.lastUpdateCheckUtc)}` : 'not checked yet']);
          updateLine.role = 'muted';
        }

        const items = hostAttention(d);
        attention.set(items);
        attentionEmpty.update(items.length);

        tenantList.set(d.tenants);
        tenantEmpty.update(d.tenants.length);

        events.set(d.recentEvents);
        eventsEmpty.update(d.recentEvents.length);
      }

      async function load(): Promise<void> {
        if (!body.visible) status.busy('Loading…');
        try {
          const d = await host.dashboard(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          render(d);
          status.clear();
          body.visible = true;
          relayout(body);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the dashboard: ' + describeError(error));
        }
      }

      ctx.dispose.interval(() => void load(), REFRESH_MS);
      void load();
    }
  };
}
